import { Reducer } from '@reduxjs/toolkit';
import { getReducerError, ReducerError } from '../Utils/reducerError';
import {
  ActivityStatus,
  ActivityStatusMap,
  ActivityStatusRecord,
  ActivityStatusSocketError,
  ActivityStatusSocketStatus,
} from '../Utils/types';
import createUuid from '../Utils/createUuid';

export type ActivityStatusState = {
  socketStatus: ActivityStatusSocketStatus;
  socketError?: ActivityStatusSocketError;
  activityStatuses: Record<number, ActivityStatus>;
  reducerErrors: Array<ReducerError>;
};

const initialState: ActivityStatusState = {
  socketStatus: 'unknown',
  socketError: undefined,
  activityStatuses: [],
  reducerErrors: [],
};

export type ActivityStatusAction =
  | TriggerStatusSocket
  | SetStatusSocketStatus
  | ReceiveActivityStatuses
  | AddActivityStatusSub
  | RemoveActivityStatusSub
  | StatusUpdate
  | StatusHeartbeat
  | ActivityWakeUp
  | HydrateActivityStatuses
  | SubscribeContactListActivityStatuses
  | ReceiveContactListActivityStatuses;

export type GetInitialStatuses = {
  type: 'ActivityStatus/GET_INITIAL_STATUSES';
  userId: number;
};

export type AddActivityStatusSub = {
  type: 'ActivityStatus/ADD_ACTIVITY_STATUS_SUB';
  userId: number;
};

export type RemoveActivityStatusSub = {
  type: 'ActivityStatus/REMOVE_ACTIVITY_STATUS_SUB';
  userId: number;
};

export type ReceiveActivityStatuses = {
  type: 'ActivityStatus/RECEIVE_ACTIVITY_STATUSES';
  statuses: ActivityStatusMap;
};

export type StatusHeartbeat = {
  type: 'ActivityStatus/STATUS_HEARTBEAT';
  userId: number;
};

export type ActivityWakeUp = {
  type: 'ActivityStatus/ACTIVITY_WAKE_UP';
  userId: number;
};

export type StatusUpdate = {
  type: 'ActivityStatus/STATUS_UPDATE';
  userId: number;
  status: ActivityStatus;
};

export type SubscribeContactListActivityStatuses = {
  type: 'ActivityStatus/SUBSCRIBE_CONTACT_LIST_STATUSES';
  userId: number;
};

export type ReceiveContactListActivityStatuses = {
  type: 'ActivityStatus/RECEIVE_CONTACT_LIST_STATUSES';
  statuses: ActivityStatusMap;
};

export type HydrateActivityStatuses = {
  type: 'ActivityStatus/HYDRATE_ACTIVITY_STATUSES';
  userId: number;
  statusIds: number[];
};

export type TriggerStatusSocket = {
  type: 'ActivityStatus/SOCKET';
};

export type SetStatusSocketStatus = {
  type: 'ActivityStatus/SOCKET_STATUS';
  status: ActivityStatusSocketStatus;
  error?: ActivityStatusSocketError;
};

export const addActivityStatusSub = (userId: number): AddActivityStatusSub => ({
  type: 'ActivityStatus/ADD_ACTIVITY_STATUS_SUB',
  userId,
});

export const removeActivityStatusSub = (userId: number): RemoveActivityStatusSub => ({
  type: 'ActivityStatus/REMOVE_ACTIVITY_STATUS_SUB',
  userId,
});

export const callStatusHeartbeat = (userId: number): StatusHeartbeat => ({
  type: 'ActivityStatus/STATUS_HEARTBEAT',
  userId,
});

export const callActivityWakeUp = (userId: number): ActivityWakeUp => ({
  type: 'ActivityStatus/ACTIVITY_WAKE_UP',
  userId,
});

export const subscribeContactListActivityStatuses = (userId: number): SubscribeContactListActivityStatuses => ({
  type: 'ActivityStatus/SUBSCRIBE_CONTACT_LIST_STATUSES',
  userId,
});

export const hydrateActivityStatuses = (userId: number, statusIds: number[]): HydrateActivityStatuses => ({
  type: 'ActivityStatus/HYDRATE_ACTIVITY_STATUSES',
  userId,
  statusIds,
});

export const triggerStatusSocket = (): TriggerStatusSocket => ({
  type: 'ActivityStatus/SOCKET',
});

export const setStatusSocketState = (
  status: ActivityStatusSocketStatus,
  error: ActivityStatusSocketError | undefined = undefined,
): SetStatusSocketStatus => ({
  type: 'ActivityStatus/SOCKET_STATUS',
  status,
  error,
});

export const receiveActivityStatuses = (statuses: ActivityStatusMap): ReceiveActivityStatuses => ({
  type: 'ActivityStatus/RECEIVE_ACTIVITY_STATUSES',
  statuses,
});

export const receiveContactListActivityStatuses = (
  statuses: ActivityStatusMap,
): ReceiveContactListActivityStatuses => ({
  type: 'ActivityStatus/RECEIVE_CONTACT_LIST_STATUSES',
  statuses,
});

export const updateActivityStatus = (userId: number, status: ActivityStatus): StatusUpdate => ({
  type: 'ActivityStatus/STATUS_UPDATE',
  userId: userId,
  status,
});

const uniqueStatusMapper = (statuses: ActivityStatusRecord, newStatuses: ActivityStatusMap): ActivityStatusRecord => {
  newStatuses.forEach(({ user_id, status }) => {
    statuses[user_id] = status;
  });

  return statuses;
};

const reducer = (state: ActivityStatusState = initialState, action: ActivityStatusAction): ActivityStatusState => {
  // wrap the whole reducer to catch any unexpected exceptions and record them
  // Another component will need to act upon these and potentially send them to Sentry
  try {
    return innerReducer(state, action);
  } catch (err) {
    const reducerErrors = state.reducerErrors.slice();
    const uuid = createUuid(0);
    if (err instanceof Error) {
      reducerErrors.push(getReducerError(uuid, 'activityStatus', action, err));
    } else {
      const unknownError = new Error('An unknown error occurred in ActivityStatusActions');
      reducerErrors.push(getReducerError(uuid, 'activityStatus', action, unknownError));
    }
    return {
      ...state,

      reducerErrors,
    };
  }
};

const innerReducer = (state: ActivityStatusState = initialState, action: ActivityStatusAction): ActivityStatusState => {
  switch (action.type) {
    case 'ActivityStatus/SOCKET_STATUS': {
      return {
        ...state,

        socketStatus: action.status,
        socketError: action.error,
      };
    }

    case 'ActivityStatus/RECEIVE_ACTIVITY_STATUSES': {
      const uniqueStatuses = uniqueStatusMapper(state.activityStatuses, action.statuses);

      return {
        ...state,
        activityStatuses: uniqueStatuses,
      };
    }

    case 'ActivityStatus/RECEIVE_CONTACT_LIST_STATUSES': {
      const uniqueStatuses = uniqueStatusMapper(state.activityStatuses, action.statuses);

      return {
        ...state,
        activityStatuses: uniqueStatuses,
      };
    }

    case 'ActivityStatus/STATUS_UPDATE': {
      const status: ActivityStatusMap = [
        {
          user_id: action.userId,
          status: action.status,
        },
      ];

      const uniqueStatuses = uniqueStatusMapper(state.activityStatuses, status);

      return {
        ...state,
        activityStatuses: uniqueStatuses,
      };
    }

    default:
      return state;
  }
};

export default reducer as Reducer<ActivityStatusState>;
